import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { GeofenceMap } from "@/components/GeofenceMap";
import { toast } from "sonner";
import { MagnifyingGlass, Crosshair } from "@phosphor-icons/react";

const EMPTY = { name: "", kind: "posto", ref_id: "", address: "", city: "", uf: "", lat: null, lng: null, radius_m: 300 };

export function GeofenceDialog({ item, onClose, onSaved }) {
  const [form, setForm] = useState(EMPTY);
  const [refs, setRefs] = useState([]);
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e?.target ? e.target.value : e }));

  useEffect(() => {
    if (!item) return;
    setForm({ ...EMPTY, ...item });
    setResults([]);
    Promise.all([api.get("/clients"), api.get("/suppliers")]).then(([c, s]) => setRefs([
      ...c.data.map((x) => ({ id: x.id, kind: "cliente", name: x.name, address: x.address, city: x.city, uf: x.uf })),
      ...s.data.map((x) => ({ id: x.id, kind: x.type === "oficina" ? "oficina" : "posto", name: x.name, address: x.address, city: x.city, uf: x.state })),
    ])).catch(() => {});
  }, [item]);

  const pickRef = (id) => {
    const r = refs.find((x) => x.id === id);
    if (r) setForm((f) => ({ ...f, ref_id: r.id, kind: r.kind, name: r.name, address: r.address || "", city: r.city || "", uf: r.uf || "" }));
  };

  const search = async () => {
    const q = [form.address, form.city, form.uf].filter(Boolean).join(", ") || form.name;
    if (!q || q.length < 3) return toast.error("Informe endereço ou cidade");
    setBusy(true);
    try {
      let { data } = await api.get("/geocode", { params: { q } });
      if (!data.length && form.city) data = (await api.get("/geocode", { params: { q: `${form.city}, ${form.uf}` } })).data;
      setResults(data);
      if (data[0]) setForm((f) => ({ ...f, lat: data[0].lat, lng: data[0].lng }));
      else toast.error("Endereço não encontrado — toque no mapa para marcar");
    } catch { toast.error("Falha na busca"); } finally { setBusy(false); }
  };

  const here = () => navigator.geolocation?.getCurrentPosition(
    (p) => setForm((f) => ({ ...f, lat: p.coords.latitude, lng: p.coords.longitude })),
    () => toast.error("Não foi possível pegar sua localização"), { enableHighAccuracy: true, timeout: 15000 });

  const save = async (e) => {
    e.preventDefault();
    if (!form.name) return toast.error("Informe o nome");
    if (form.lat == null) return toast.error("Marque o local no mapa");
    const payload = { ...form, radius_m: Number(String(form.radius_m).replace(",", ".")) || 300, ref_id: form.ref_id || null };
    try {
      if (item?.id) await api.put(`/geofences/${item.id}`, payload); else await api.post("/geofences", payload);
      toast.success("Cerca salva");
      onSaved();
    } catch (err) { toast.error(err.response?.data?.detail || "Erro ao salvar"); }
  };

  return (
    <Dialog open={!!item} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="geofence-dialog">
        <DialogHeader><DialogTitle>{item?.id ? "Editar cerca" : "Nova cerca"}</DialogTitle></DialogHeader>
        <form onSubmit={save} className="grid grid-cols-1 lg:grid-cols-2 gap-3">
          {refs.length > 0 && (
            <div className="col-span-2"><Label>Puxar de um cliente/posto cadastrado (opcional)</Label>
              <Select value={form.ref_id || ""} onValueChange={pickRef}>
                <SelectTrigger data-testid="geofence-ref"><SelectValue placeholder="Selecione" /></SelectTrigger>
                <SelectContent>{refs.map((r) => <SelectItem key={r.id} value={r.id}>{r.name} ({r.kind})</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2"><Label>Nome do local</Label><Input value={form.name} onChange={set("name")} placeholder="Ex: Posto Trevão BR-364" data-testid="geofence-name" /></div>
          <div><Label>Tipo</Label>
            <Select value={form.kind} onValueChange={set("kind")}>
              <SelectTrigger data-testid="geofence-kind"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="posto">Posto</SelectItem><SelectItem value="cliente">Cliente</SelectItem><SelectItem value="oficina">Oficina</SelectItem><SelectItem value="outro">Outro</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>Raio (metros)</Label><Input inputMode="numeric" value={form.radius_m} onChange={set("radius_m")} data-testid="geofence-radius" /></div>
          <div className="col-span-2"><Label>Endereço</Label><Input value={form.address} onChange={set("address")} placeholder="Rua, número, rodovia, km..." data-testid="geofence-address" /></div>
          <div><Label>Cidade</Label><Input value={form.city} onChange={set("city")} data-testid="geofence-city" /></div>
          <div><Label>UF</Label><Input value={form.uf} maxLength={2} onChange={(e) => setForm((f) => ({ ...f, uf: e.target.value.toUpperCase() }))} data-testid="geofence-uf" /></div>
          <div className="col-span-2 flex flex-wrap gap-2">
            <Button type="button" variant="outline" onClick={search} disabled={busy} data-testid="btn-geofence-search" className="gap-1.5"><MagnifyingGlass size={16} /> {busy ? "Buscando..." : "Buscar no mapa"}</Button>
            <Button type="button" variant="outline" onClick={here} data-testid="btn-geofence-here" className="gap-1.5"><Crosshair size={16} /> Estou aqui agora</Button>
          </div>
          {results.length > 1 && (
            <div className="col-span-2"><Label>Resultados</Label>
              <Select onValueChange={(i) => setForm((f) => ({ ...f, lat: results[i].lat, lng: results[i].lng }))}>
                <SelectTrigger data-testid="geofence-results"><SelectValue placeholder={`${results.length} encontrados — escolha`} /></SelectTrigger>
                <SelectContent>{results.map((r, i) => <SelectItem key={i} value={String(i)}>{r.label.slice(0, 80)}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          )}
          <div className="col-span-2">
            <GeofenceMap lat={form.lat} lng={form.lng} radius={Number(form.radius_m) || 300} onPick={(lat, lng) => setForm((f) => ({ ...f, lat, lng }))} />
            <p className="text-xs text-slate-500 mt-1">Toque no mapa para ajustar o ponto exato.</p>
          </div>
          <DialogFooter className="col-span-2"><Button type="submit" data-testid="btn-save-geofence" className="bg-blue-600 hover:bg-blue-700 text-white">Salvar</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
